import { useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Store, TrendingUp, Crown } from 'lucide-react';
import type { MarketPartner } from '@/hooks/useMarketPartners';
import { trackPartnerClicked } from './giftStoreAnalytics';
import { getLocalizedCategory } from './giftStoreCategories';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const partnerCardCopy = {
  en: { visit: 'Visit Partner', trending: 'Trending', featured: 'Featured', exclusive: 'Exclusive', emptyTitle: 'More partners coming soon.', emptyDesc: 'We only add brands we\'d feel good recommending to someone we love.', emptyCheck: 'Check back soon' },
  es: { visit: 'Visitar Socio', trending: 'Tendencia', featured: 'Destacado', exclusive: 'Exclusivo', emptyTitle: 'Más socios próximamente.', emptyDesc: 'Solo añadimos marcas que recomendaríamos con confianza a alguien que amamos.', emptyCheck: 'Vuelve pronto' },
  fr: { visit: 'Visiter le Partenaire', trending: 'Tendance', featured: 'En vedette', exclusive: 'Exclusif', emptyTitle: 'Plus de partenaires bientôt.', emptyDesc: 'Nous n\'ajoutons que des marques que nous recommanderions à quelqu\'un que nous aimons.', emptyCheck: 'Revenez bientôt' },
  pt: { visit: 'Visitar Parceiro', trending: 'Em Alta', featured: 'Destaque', exclusive: 'Exclusivo', emptyTitle: 'Mais parceiros em breve.', emptyDesc: 'Só adicionamos marcas que recomendaríamos a quem amamos.', emptyCheck: 'Volte em breve' },
  ar: { visit: 'زيارة الشريك', trending: 'رائج', featured: 'مميز', exclusive: 'حصري', emptyTitle: 'المزيد من الشركاء قريبًا.', emptyDesc: 'نضيف فقط العلامات التي نوصي بها لمن نحب.', emptyCheck: 'عد قريبًا' },
  ja: { visit: 'パートナーを訪問', trending: 'トレンド', featured: '注目', exclusive: '限定', emptyTitle: 'パートナーが近日追加されます。', emptyDesc: '大切な人にも勧めたいブランドだけを厳選しています。', emptyCheck: 'またチェックしてください' },
  ko: { visit: '파트너 방문', trending: '인기', featured: '추천', exclusive: '독점', emptyTitle: '더 많은 파트너가 곧 추가됩니다.', emptyDesc: '사랑하는 사람에게 추천할 수 있는 브랜드만 선별합니다.', emptyCheck: '곧 다시 확인해주세요' },
  id: { visit: 'Kunjungi Mitra', trending: 'Tren', featured: 'Unggulan', exclusive: 'Eksklusif', emptyTitle: 'Lebih banyak mitra segera hadir.', emptyDesc: 'Kami hanya menambahkan merek yang kami rekomendasikan untuk orang yang kami cintai.', emptyCheck: 'Kembali lagi nanti' },
};

// Partner descriptions are currently stored in the database in English only; for a small curated list
// we localize them here so language switching applies consistently across the Gift Store UI.
const partnerDescriptionCopy: Record<string, Record<string, string> & { en: string }> = {
  'gray-matter': {
    en: 'A daily plant-based drink for calm, focused energy — without the prescription or the side effects.',
    pt: 'Uma bebida vegetal diária para calma e energia focada — sem receita e sem efeitos colaterais.',
    es: 'Una bebida vegetal diaria para calma y energía enfocada — sin receta ni efectos secundarios.',
    fr: 'Une boisson végétale quotidienne pour un calme et une énergie concentrée — sans ordonnance ni effets secondaires.',
    ar: 'مشروب نباتي يومي للهدوء والطاقة المركّزة — بدون وصفة طبية أو آثار جانبية.',
    ja: '落ち着きと集中力のあるエネルギーを支える、植物由来の毎日のドリンク。処方薬や副作用に頼りません。',
    ko: '처방전이나 부작용 없이, 차분함과 집중 에너지를 돕는 데일리 식물 기반 드링크.',
    id: 'Minuman nabati harian untuk ketenangan dan energi fokus — tanpa resep dan tanpa efek samping.',
  },
  'true-sea-moss': {
    en: 'Sea moss powered wellness that helps boost energy, sharpen focus, and support digestion. Wildcrafted and 100% natural.',
    pt: 'Bem-estar com sea moss que ajuda a aumentar a energia, melhorar o foco e apoiar a digestão. Selvagem e 100% natural.',
    es: 'Bienestar con sea moss que ayuda a aumentar la energía, mejorar el enfoque y apoyar la digestión. Recolectado de forma silvestre y 100% natural.',
    fr: 'Le bien-être au sea moss qui aide à booster l’énergie, affiner la concentration et soutenir la digestion. Récolté à l’état sauvage et 100% naturel.',
    ar: 'عافية مدعّمة بالـ sea moss تساعد على زيادة الطاقة، تحسين التركيز، ودعم الهضم. مُستخلصة من الطبيعة و100% طبيعية.',
    ja: '海藻（シーモス）の力で、エネルギー・集中力・消化をサポート。天然由来で100%ナチュラル。',
    ko: '씨모스 기반 웰니스로 에너지 증진, 집중력 향상, 소화 지원에 도움을 줍니다. 야생 채취, 100% 자연 원료.',
    id: 'Wellness bertenaga sea moss yang membantu meningkatkan energi, mempertajam fokus, dan mendukung pencernaan. Dipanen liar dan 100% alami.',
  },
  floristone: {
    en: 'Fresh flowers delivered with care — because sometimes the best way to say it is with a bouquet that speaks for you.',
    pt: 'Flores frescas entregues com carinho — porque às vezes a melhor forma de dizer é com um buquê que fala por você.',
    es: 'Flores frescas entregadas con cuidado — porque a veces la mejor manera de decirlo es con un ramo que hable por ti.',
    fr: 'Des fleurs fraîches livrées avec soin — parce que parfois, la meilleure façon de le dire, c’est avec un bouquet qui parle pour vous.',
    ar: 'زهور طازجة تُسلّم بعناية — لأن أفضل طريقة للقول أحياناً هي باقة تتحدث نيابةً عنك.',
    ja: '心を込めて届けるフレッシュな花々。時には、あなたの代わりに語ってくれる花束がいちばん。',
    ko: '정성껏 배송되는 신선한 꽃 — 때로는 당신을 대신해 말해주는 꽃다발이 최고의 표현입니다.',
    id: 'Bunga segar yang dikirim dengan penuh perhatian — karena terkadang cara terbaik untuk mengatakannya adalah dengan buket yang berbicara untukmu.',
  },
  'hume-health': {
    en: "The Hume Band shows you what's happening inside your body — not just how hard you worked. Real-time health insights powered by science.",
    pt: 'A Hume Band mostra o que está acontecendo dentro do seu corpo — não só o quanto você se esforçou. Insights de saúde em tempo real, com base científica.',
    es: 'La Hume Band te muestra lo que está pasando dentro de tu cuerpo — no solo cuánto te esforzaste. Información de salud en tiempo real, basada en ciencia.',
    fr: 'Le bracelet Hume vous montre ce qui se passe dans votre corps — pas seulement à quel point vous avez travaillé. Des infos santé en temps réel, fondées sur la science.',
    ar: 'سوار Hume يوضح ما يحدث داخل جسمك — وليس فقط مدى جهدك. رؤى صحية لحظية مدعومة بالعلم.',
    ja: 'Hume Bandは、どれだけ頑張ったかだけでなく「体の内側で何が起きているか」を可視化。科学に基づくリアルタイムの健康インサイト。',
    ko: 'Hume Band는 얼마나 열심히 했는지뿐 아니라 몸속에서 무슨 일이 일어나는지 보여줍니다. 과학 기반 실시간 건강 인사이트.',
    id: 'Hume Band menunjukkan apa yang terjadi di dalam tubuhmu — bukan hanya seberapa keras kamu berlatih. Insight kesehatan real-time berbasis sains.',
  },
};

interface PartnerCardProps {
  partner: MarketPartner;
  index: number;
  trending?: boolean;
}

function PartnerInitials({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
      <span className="font-bold text-primary text-lg tracking-tight">{initials}</span>
    </div>
  );
}

function PartnerLogo({ logoUrl, brandName }: { logoUrl: string | null; brandName: string }) {
  const [imgError, setImgError] = useState(false);

  if (!logoUrl || imgError) {
    return <PartnerInitials name={brandName} />;
  }

  return (
    <img
      src={logoUrl}
      alt={`${brandName} logo`}
      className="w-14 h-14 rounded-xl object-contain border border-border bg-white p-2 shrink-0"
      onError={() => setImgError(true)}
    />
  );
}

function buildGoUrl(partner: MarketPartner): string {
  const params = new URLSearchParams({
    utm_source: 'gift_store',
    utm_medium: 'affiliate',
    utm_campaign: 'market',
    utm_content: partner.category_slug,
    placement: 'gift_store',
  });
  return `/go/${partner.slug}?${params.toString()}`;
}

export function PartnerCard({ partner, index, trending = false }: PartnerCardProps) {
  const { language } = useLanguage();
  const c = lc(partnerCardCopy, language);
  const localizedCat = getLocalizedCategory(partner.category_slug, language);
  const goUrl = buildGoUrl(partner);
  const localizedDesc = lc<string>(
    (partnerDescriptionCopy[partner.slug] ?? { en: partner.short_description }) as any,
    language,
  );

  const handleVisit = () => {
    trackPartnerClicked(partner.slug, partner.brand_name, partner.category_slug);
    window.open(goUrl, '_blank');
  };

  const isExclusive = partner.category_slug === 'healing-clarity' || partner.featured;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.35 }}
      className={cn(
        'group relative flex flex-col gap-4 p-5 rounded-2xl border bg-card text-card-foreground',
        'transition-all duration-200',
        'hover:-translate-y-1.5 hover:shadow-xl hover:border-primary/40',
      )}
    >
      {/* Badges */}
      <div className="flex flex-wrap items-center gap-1 mb-1">
        {trending && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-widest uppercase text-amber-600 bg-amber-500/10 rounded-full px-2 py-0.5">
            <TrendingUp className="h-2.5 w-2.5" />
            {c.trending}
          </span>
        )}
        {partner.featured && !trending && (
          <span className="text-[10px] font-semibold tracking-widest uppercase text-primary bg-primary/10 rounded-full px-2 py-0.5">
            {c.featured}
          </span>
        )}
        {isExclusive && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-widest uppercase text-amber-600 bg-amber-500/10 rounded-full px-2 py-0.5">
            <Crown className="h-2.5 w-2.5" />
            {c.exclusive}
          </span>
        )}
      </div>

      <div className="flex items-center gap-3">
        <PartnerLogo logoUrl={partner.logo_url} brandName={partner.brand_name} />
        <div>
          <h3 className="font-semibold text-foreground leading-tight">{partner.brand_name}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{localizedCat.title}</p>
        </div>
      </div>

      <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
        {localizedDesc}
      </p>

      <a
        href={goUrl}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={(e) => {
          e.preventDefault();
          handleVisit();
        }}
        className={cn(
          'inline-flex items-center justify-center gap-2 w-full rounded-md text-sm font-medium',
          'h-9 px-3 transition-colors',
          'bg-primary text-primary-foreground hover:bg-primary/90',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        )}
      >
        <ExternalLink className="h-3.5 w-3.5" />
        {c.visit}
      </a>
    </motion.div>
  );
}

export function PartnerEmptyState() {
  const { language } = useLanguage();
  const c = lc(partnerCardCopy, language);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="col-span-full flex flex-col items-center gap-3 py-16 text-center"
    >
      <Store className="h-10 w-10 text-muted-foreground/40" />
      <h3 className="text-base font-semibold text-foreground">{c.emptyTitle}</h3>
      <p className="text-sm text-muted-foreground max-w-xs">{c.emptyDesc}</p>
      <span className="text-xs text-muted-foreground italic mt-1">{c.emptyCheck}</span>
    </motion.div>
  );
}
